<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { api, messageFrom } from '../api'
import type { Generation, PresetRow, Theme } from '../types'

const themes = ref<Theme[]>([])
const samplePhoto = ref<File | null>(null)
const saving = ref('')
const checking = ref('')
const checkResults = ref<Record<string, Generation>>({})
const saved = ref('')
const error = ref('')
const loading = ref(true)
const pollTimers = new Set<number>()

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try { themes.value = (await api<{ themes: Theme[] }>('/api/admin/catalog')).themes }
  catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

function choosePhoto(event: Event): void {
  samplePhoto.value = (event.target as HTMLInputElement).files?.[0] ?? null
}

async function save(preset: PresetRow): Promise<void> {
  saving.value = preset.code
  saved.value = ''
  error.value = ''
  try {
    const response = await api<{ preset: PresetRow }>(`/api/admin/presets/${encodeURIComponent(preset.code)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        title: preset.title,
        scenePrompt: preset.scene_prompt,
        sort: Number(preset.sort),
        isActive: preset.is_active,
        isPremium: preset.is_premium,
      }),
    })
    Object.assign(preset, response.preset)
    saved.value = preset.code
  } catch (reason) { error.value = messageFrom(reason) }
  finally { saving.value = '' }
}

async function poll(presetCode: string, generationId: string): Promise<void> {
  try {
    const response = await api<{ generation: Generation }>(`/api/admin/generations/${generationId}`)
    checkResults.value[presetCode] = response.generation
    if (response.generation.status === 'queued' || response.generation.status === 'processing') {
      const timer = window.setTimeout(() => {
        pollTimers.delete(timer)
        void poll(presetCode, generationId)
      }, 1_500)
      pollTimers.add(timer)
    } else if (checking.value === presetCode) checking.value = ''
  } catch (reason) {
    error.value = messageFrom(reason)
    checking.value = ''
  }
}

async function check(preset: PresetRow): Promise<void> {
  if (!samplePhoto.value) {
    error.value = 'Сначала выбери фото-образец вверху.'
    return
  }
  checking.value = preset.code
  error.value = ''
  delete checkResults.value[preset.code]
  try {
    const body = new FormData()
    body.append('photo', samplePhoto.value)
    body.append('title', preset.title)
    body.append('kind', preset.kind)
    body.append('scenePrompt', preset.scene_prompt)
    body.append('aspectRatio', preset.aspect_ratio)
    const response = await api<{ generationId: string }>(
      `/api/admin/presets/${encodeURIComponent(preset.code)}/check`,
      { method: 'POST', body },
    )
    await poll(preset.code, response.generationId)
  } catch (reason) {
    error.value = messageFrom(reason)
    checking.value = ''
  }
}

onMounted(() => void load())
onBeforeUnmount(() => { for (const timer of pollTimers) window.clearTimeout(timer) })
</script>

<template>
  <main class="page">
    <div class="page-title">
      <div><p class="eyebrow">Каталог</p><h2>Пресеты</h2></div>
      <label class="sample-picker">Фото-образец<input type="file" accept="image/jpeg,image/png,image/webp" @change="choosePhoto" /></label>
    </div>
    <p class="muted">Правки сохраняются в БД. «Проверить» использует текст из поля прямо сейчас, даже до сохранения.</p>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="muted">Загружаю каталог…</p>
    <div v-else class="catalog-tree">
      <details v-for="theme in themes" :key="theme.code" class="tree-theme" open>
        <summary>{{ theme.emoji }} {{ theme.title }}</summary>
        <details v-for="subtheme in theme.subthemes" :key="subtheme.code" class="tree-subtheme">
          <summary>{{ subtheme.title }} <small>{{ subtheme.presets.length }} пресетов</small></summary>
          <article v-for="preset in subtheme.presets" :key="preset.code" class="preset-editor">
            <div class="preset-heading">
              <span class="preset-emoji">{{ preset.emoji }}</span>
              <label>Название<input v-model="preset.title" maxlength="80" /></label>
              <label class="short-field">Порядок<input v-model.number="preset.sort" type="number" /></label>
              <span class="kind-badge">{{ preset.kind }}</span>
            </div>
            <label>Scene prompt
              <textarea v-model="preset.scene_prompt" rows="5" spellcheck="false" />
            </label>
            <div class="preset-actions">
              <label><input v-model="preset.is_active" type="checkbox" /> Включён</label>
              <label><input v-model="preset.is_premium" type="checkbox" /> Премиум</label>
              <span v-if="preset.supports_pair" class="muted">Поддерживает двоих</span>
              <button class="button button--quiet" type="button" :disabled="saving === preset.code" @click="save(preset)">
                {{ saving === preset.code ? 'Сохраняю…' : saved === preset.code ? 'Сохранено ✓' : 'Сохранить' }}
              </button>
              <button class="button button--primary" type="button" :disabled="checking === preset.code" @click="check(preset)">
                {{ checking === preset.code ? 'Рисую…' : 'Проверить' }}
              </button>
            </div>
            <div v-if="checkResults[preset.code]" class="preset-check">
              <img
                v-if="checkResults[preset.code]?.status === 'completed'"
                :src="`/media/generations/${checkResults[preset.code]?.id}`" :alt="preset.title"
              />
              <p v-else-if="checkResults[preset.code]?.status === 'failed'" class="error">
                {{ checkResults[preset.code]?.error }}
              </p>
              <p v-else class="muted">В очереди / рисую…</p>
              <p v-if="checkResults[preset.code]?.cost_usd !== null">
                Факт: {{ (Number(checkResults[preset.code]?.cost_usd) * 80).toFixed(2) }} ₽ ·
                {{ ((checkResults[preset.code]?.runtime_ms ?? 0) / 1000).toFixed(1) }} с
              </p>
            </div>
          </article>
        </details>
      </details>
    </div>
  </main>
</template>
